// ⬡B:aba_shared.ccwa_core.components.three_panel:CODE:resizable_glass_layout:20260420⬡
// ⬡B:aba_shared.ccwa_core.components.three_panel.responsive:CODE:s18_mobile_drawer_collapse:20260430⬡
// ⬡B:aba_shared.ccwa_core.components.three_panel.auto_responsive:CODE:s19_skin_inheritance_default:20260430⬡
// Three-panel layout: Agents-working on the left, Chat in the middle, Code preview on the right.
// Resizable dividers on desktop (drag to resize). Glass morphism (semi-transparent, backdrop-blur).
// Ken Burns animated background behind everything. Glow effect on the frame when ABA is speaking.
//
// Session 18 added layoutMode-aware responsive collapse:
//   - desktop: original behavior, three panels visible, drag dividers to resize.
//   - tablet:  center + left visible; right collapses into a slide-out drawer.
//   - mobile:  center is full-width; left AND right become slide-out drawers.
// CSS in aba.css under @media blocks reads .aba-mobile / .aba-tablet /
// .aba-drawer-left-open / .aba-drawer-right-open classes on the root.
// JS responsibility here: mount the toggle pills, render the scrim, manage drawer state.
//
// Session 19 made responsive the DEFAULT for skin inheritance:
//   - If a skin does NOT pass layoutMode, this component calls useViewportBreakpoint()
//     internally so all skins (CCWA, MyABA CIP, OneABA CIB, AWA Portal) get responsive
//     layout for free without the skin author having to wire it up.
//   - To opt OUT (force desktop on all viewports), pass layoutMode="desktop" explicitly.
//   - CARA owns this component per agent JD. SHADOW gates skins that import this
//     file; see services/shadow/shadowSkinResponsiveCheck.js on ababase.

import { useState, useRef, useCallback, useEffect } from 'react';
import { useViewportBreakpoint } from '../hooks/useViewportBreakpoint.js';

export function ThreePanelLayout({
  leftPanel,
  centerPanel,
  rightPanel,
  glowing = false,
  backgroundImage = null,
  leftDefaultPct = 22,
  rightDefaultPct = 32,
  layoutMode  // undefined => auto-detect via useViewportBreakpoint; pass 'desktop'/'tablet'/'mobile' to override
}) {
  // S19 skin-inheritance default: if skin didn't pass layoutMode, detect from viewport.
  // We call the hook UNCONDITIONALLY (rules of hooks) and only use the value if needed.
  const detected = useViewportBreakpoint();
  const effectiveLayoutMode = layoutMode || detected.mode;

  const [leftPct, setLeftPct] = useState(leftDefaultPct);
  const [rightPct, setRightPct] = useState(rightDefaultPct);
  const [drawerLeftOpen, setDrawerLeftOpen] = useState(false);
  const [drawerRightOpen, setDrawerRightOpen] = useState(false);
  const containerRef = useRef(null);
  const draggingRef = useRef(null);

  const isMobile = effectiveLayoutMode === 'mobile';
  const isTablet = effectiveLayoutMode === 'tablet';
  const isResponsive = isMobile || isTablet;

  // Close drawers if we transition back to desktop mid-session.
  useEffect(() => {
    if (effectiveLayoutMode === 'desktop') {
      setDrawerLeftOpen(false);
      setDrawerRightOpen(false);
    }
    // On tablet, left panel is always visible inline — no left drawer.
    if (isTablet) {
      setDrawerLeftOpen(false);
    }
  }, [effectiveLayoutMode, isTablet]);

  // Close right drawer on Escape for keyboard users.
  useEffect(() => {
    if (!drawerLeftOpen && !drawerRightOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setDrawerLeftOpen(false);
        setDrawerRightOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [drawerLeftOpen, drawerRightOpen]);

  const startDrag = useCallback((which) => (e) => {
    // Drag-to-resize is desktop-only. Ignore on mobile/tablet.
    if (effectiveLayoutMode !== 'desktop') return;
    e.preventDefault();
    draggingRef.current = which;
    const onMove = (ev) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const xFromLeft = ev.clientX - rect.left;
      const pct = (xFromLeft / rect.width) * 100;
      if (draggingRef.current === 'left') {
        const clamped = Math.max(10, Math.min(45, pct));
        setLeftPct(clamped);
      } else if (draggingRef.current === 'right') {
        // Right divider: percentage from left; right panel width is 100 - pct
        const clamped = Math.max(55, Math.min(90, pct));
        setRightPct(100 - clamped);
      }
    };
    const onUp = () => {
      draggingRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [effectiveLayoutMode]);

  const centerPct = 100 - leftPct - rightPct;

  // Compose root className: base + mode + drawer state.
  const rootClass = [
    'aba-three-panel',
    glowing ? 'aba-glowing' : '',
    isMobile ? 'aba-mobile' : '',
    isTablet ? 'aba-tablet' : '',
    drawerLeftOpen ? 'aba-drawer-left-open' : '',
    drawerRightOpen ? 'aba-drawer-right-open' : ''
  ].filter(Boolean).join(' ');

  // On mobile/tablet, panel widths are controlled by CSS @media rules,
  // not inline style. Avoid setting width style that overrides them.
  const leftStyle  = effectiveLayoutMode === 'desktop' ? { width: `${leftPct}%` }   : undefined;
  const centerStyle= effectiveLayoutMode === 'desktop' ? { width: `${centerPct}%` } : undefined;
  const rightStyle = effectiveLayoutMode === 'desktop' ? { width: `${rightPct}%` }  : undefined;

  const closeDrawers = useCallback(() => {
    setDrawerLeftOpen(false);
    setDrawerRightOpen(false);
  }, []);

  return (
    <div className={rootClass}>
      {backgroundImage && (
        <div
          className="aba-kenburns-bg"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
      )}

      {/* Mobile: left drawer toggle pill (☰) over center panel */}
      {isMobile && (
        <button
          type="button"
          className="aba-drawer-toggle left"
          onClick={() => setDrawerLeftOpen(v => !v)}
          aria-label={drawerLeftOpen ? 'Close agents panel' : 'Open agents panel'}
          aria-expanded={drawerLeftOpen}
        >
          ☰
        </button>
      )}

      {/* Mobile + tablet: right drawer toggle pill */}
      {isResponsive && (
        <button
          type="button"
          className="aba-drawer-toggle right"
          onClick={() => setDrawerRightOpen(v => !v)}
          aria-label={drawerRightOpen ? 'Close panel' : 'Open panel'}
          aria-expanded={drawerRightOpen}
        >
          ⊞
        </button>
      )}

      {/* Scrim overlay when any drawer is open; tap to close */}
      {(drawerLeftOpen || drawerRightOpen) && (
        <div
          className="aba-drawer-scrim"
          onClick={closeDrawers}
          onTouchStart={closeDrawers}
          aria-hidden="true"
        />
      )}

      <div className="aba-panel-frame" ref={containerRef}>
        <div
          className="aba-panel aba-panel-left"
          style={leftStyle}
        >
          {leftPanel}
        </div>
        <div
          className="aba-divider"
          onMouseDown={startDrag('left')}
          role="separator"
          aria-orientation="vertical"
        />
        <div
          className="aba-panel aba-panel-center"
          style={centerStyle}
        >
          {centerPanel}
        </div>
        <div
          className="aba-divider"
          onMouseDown={startDrag('right')}
          role="separator"
          aria-orientation="vertical"
        />
        <div
          className="aba-panel aba-panel-right"
          style={rightStyle}
        >
          {rightPanel}
        </div>
      </div>
    </div>
  );
}
